import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  FireIcon, 
  ChartBarIcon, 
  UserIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { paymentsAPI, SubscriptionInfo } from '../../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [monthlyRateCents, setMonthlyRateCents] = useState<number | null>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState(true);
  const [isBillingActionLoading, setIsBillingActionLoading] = useState(false);

  const loadBilling = async () => {
    setIsLoadingBilling(true);
    try {
      const response = await paymentsAPI.getSubscription();
      setSubscription(response.data.subscription);
      setMonthlyRateCents(response.data.monthlyRateCents);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load billing status');
    } finally {
      setIsLoadingBilling(false);
    }
  };

  useEffect(() => {
    const initBilling = async () => {
      const params = new URLSearchParams(window.location.search);
      const billing = params.get('billing');

      if (billing === 'success') {
        try {
          const response = await paymentsAPI.syncSubscription();
          setSubscription(response.data.subscription);
          setMonthlyRateCents(response.data.monthlyRateCents);
          toast.success('Subscription started successfully');
        } catch (error: any) {
          toast.error(error.response?.data?.error || 'Payment succeeded, but status sync failed. Refresh the page.');
          await loadBilling();
        }
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoadingBilling(false);
        return;
      }

      if (billing === 'cancel') {
        toast.error('Checkout canceled');
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      await loadBilling();
    };

    initBilling();
  }, []);

  const startCheckout = async () => {
    setIsBillingActionLoading(true);
    try {
      const response = await paymentsAPI.createCheckoutSession();
      if (response.data.url) {
        window.location.assign(response.data.url);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to start checkout');
      setIsBillingActionLoading(false);
    }
  };

  const runSubscriptionAction = async (action: 'pause' | 'resume' | 'cancel') => {
    if (action === 'cancel' && !window.confirm('Cancel your subscription immediately with no refund?')) {
      return;
    }

    setIsBillingActionLoading(true);
    try {
      const response =
        action === 'pause'
          ? await paymentsAPI.pauseSubscription()
          : action === 'resume'
            ? await paymentsAPI.resumeSubscription()
            : await paymentsAPI.cancelSubscription();
      setSubscription(response.data.subscription);
      toast.success(
        action === 'pause'
          ? 'Subscription paused'
          : action === 'resume'
            ? 'Subscription resumed'
            : 'Subscription canceled'
      );
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Failed to ${action} subscription`);
    } finally {
      setIsBillingActionLoading(false);
    }
  };

  const formatDateTime = (value?: string) => {
    if (!value) return null;
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const cancellationLabel = (sub: SubscriptionInfo) => {
    const who =
      sub.canceledByRole === 'TRAINER'
        ? 'trainer'
        : sub.canceledByRole === 'CLIENT'
          ? 'client'
          : 'system';
    const when = formatDateTime(sub.canceledAt);
    return when ? `Canceled by ${who} on ${when}` : `Canceled by ${who}`;
  };

  const quickActions = [
    { 
      name: 'My Workouts', 
      icon: FireIcon, 
      color: 'bg-blue-600', 
      action: () => navigate('/client/workouts'),
      description: 'View assigned workouts'
    },
    { 
      name: 'My Meals', 
      icon: UserIcon, 
      color: 'bg-green-600', 
      action: () => navigate('/client/meals'),
      description: 'View meal plans'
    },
    { 
      name: 'My Progress', 
      icon: ChartBarIcon, 
      color: 'bg-purple-600', 
      action: () => navigate('/client/progress'),
      description: 'Track your progress'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 -mt-6">
      {/* Mobile Header */}
      <div className="sticky top-16 z-20 bg-white border-b border-gray-200 px-4 py-4">
        <h1 className="text-xl font-bold text-gray-900">
          Client Dashboard
        </h1>
      </div>

      <div className="px-4 py-6 space-y-6 max-w-md mx-auto">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
              <CreditCardIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
              {isLoadingBilling ? (
                <p className="mt-2 text-sm text-gray-600">Loading billing...</p>
              ) : (
                <>
                  <p className="mt-2 text-sm text-gray-600">
                    {subscription
                      ? `Status: ${subscription.status}`
                      : monthlyRateCents
                        ? `Monthly rate: $${(monthlyRateCents / 100).toFixed(2)}`
                        : 'Your trainer has not set a monthly rate yet.'}
                  </p>

                  {subscription && (
                    <p className="mt-1 text-sm text-gray-600">
                      ${(subscription.monthlyAmountCents / 100).toFixed(2)} / month
                    </p>
                  )}

                  {subscription?.status === 'canceled' && (
                    <p className="mt-2 text-sm font-medium text-red-700">
                      {cancellationLabel(subscription)}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {(!subscription || subscription.status === 'canceled') && monthlyRateCents && (
                      <button
                        type="button"
                        disabled={isBillingActionLoading}
                        onClick={startCheckout}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {isBillingActionLoading
                          ? 'Opening checkout...'
                          : subscription?.status === 'canceled'
                            ? 'Subscribe again'
                            : 'Subscribe'}
                      </button>
                    )}

                    {subscription?.status === 'active' && (
                      <button
                        type="button"
                        disabled={isBillingActionLoading}
                        onClick={() => runSubscriptionAction('pause')}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Pause
                      </button>
                    )}

                    {subscription?.status === 'paused' && (
                      <button
                        type="button"
                        disabled={isBillingActionLoading}
                        onClick={() => runSubscriptionAction('resume')}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Resume
                      </button>
                    )}

                    {subscription && subscription.status !== 'canceled' && (
                      <button
                        type="button"
                        disabled={isBillingActionLoading}
                        onClick={() => runSubscriptionAction('cancel')}
                        className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancel now
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.name}
              onClick={action.action}
              className={`${action.color} text-white p-6 rounded-lg active:opacity-75 transition-opacity text-center`}
            >
              <action.icon className="h-8 w-8 mx-auto mb-3" />
              <div className="text-lg font-medium">{action.name}</div>
              <div className="text-sm opacity-90 mt-2">{action.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
